import type { Transacao } from '@/types/Transacao'

export const useLivroDiarioAuxiliarStore = defineStore('livroDiarioAuxiliarStore', {
  state: () => ({
    transacao: {} as Transacao,
    depositosPrevios: [],
    filtros: {
      ano: new Date().getFullYear(),
      mes: new Date().getMonth() + 1,
      tipo: 'executado',
      termoDeBusca: '',
      receitaDespesa: 'despesa',
    },
    formNovaReceita: {
      data: '',
      descricao: '',
      valor: 0,
      servicos: [],
    },
    modalAdicionarReceita: {
      exibir: false,
      receitaSelecionada: '',
      quantidade: 1,
      valor: 0,
      listaDeServicos: [],
      editMode: false,
    },
    modalCadastrarReceitas: {
      exibir: false,
      receita: {
        nome: '',
        valor: 0,
      },
      titulo: 'Cadastrar nova Receita',
      inputValor: null,
    },
    modalImportarXml: {
      exibir: false,
      mes: new Date().getMonth() + 1,
      ano: new Date().getFullYear(),
      limparRegistros: false,
    },
    modalFiltrosAvancados: {
      exibir: false,
      pesquisaRealizada: false,
      filtros: {
        vencimentoInicio: '',
        vencimentoFim: '',
        valorComparacao: 'Igual',
        valor: '',
        possuiAnexo: null,
        conta: '',
        tipo: 'despesas',
        despesas_nao_efetivas: true,
        statusPagamento: null, // null = indiferente, true = pago, false = não pago
      },
    },
    modalDownloadComprovantes: {
      exibir: false,
    },
  }),
  actions: {
    async fetchTransacao ({ ano = null, mes = null, receitaDespesa = null }: { ano?: string | null; mes?: string | null; receitaDespesa?: 'receita' | 'despesa' | null } = {}) {
      this.transacao = (await useApiStore().request(this.getTransacaoUrl({ ano, mes, receitaDespesa }), {
        method: 'GET',
      })) as Transacao
      this.transacao.transacoes = this.transacao.transacoes.sort((a, b) => {
        return new Date(a.data).getTime() - new Date(b.data).getTime()
      })
    },
    getTransacaoUrl ({ ano = null, mes = null, receitaDespesa = null }: { ano?: string | null; mes?: string | null; receitaDespesa?: 'receita' | 'despesa' | null } = {}): string {
      let url
      if (ano && mes) {
        url = `/gerencia/livro-diario-auxiliar/transacoes/${ano}/${mes}/executado?`
      } else {
        url = `/gerencia/livro-diario-auxiliar/transacoes/${this.filtros.ano}/${this.filtros.mes}/${this.filtros.tipo}?`
      }
      if (this.filtros.termoDeBusca !== '') {
        url += `busca=${this.filtros.termoDeBusca}`
      }
      if (receitaDespesa) {
        url += `&receitaDespesa=${receitaDespesa}`
      } else if (this.filtros.receitaDespesa !== 'todos') {
        url += `&receitaDespesa=${this.filtros.receitaDespesa}`
      }
      return url
    },
    async setFiltros (filtros: { mes?: number; ano?: number; tipo?: string }) {
      this.filtros = {
        ...this.filtros,
        ...filtros,
      }

      // Set default dates when competency changes
      if (filtros.mes && filtros.ano) {
        const firstDay = new Date(filtros.ano, filtros.mes - 1, 1)
        const lastDay = new Date(filtros.ano, filtros.mes, 0)

        this.modalFiltrosAvancados.filtros.vencimentoInicio = firstDay.toISOString().split('T')[0]
        this.modalFiltrosAvancados.filtros.vencimentoFim = lastDay.toISOString().split('T')[0]
      }

      await this.fetchTransacao()
    },
    async pesquisarComFiltrosAvancados (filtros: { vencimentoInicio: string; vencimentoFim: string; valor: string; valorComparacao: string, possuiAnexo: boolean | null, conta: string, statusPagamento?: boolean | null }) {
      const params = new URLSearchParams()
      const url = '/gerencia/livro-diario-auxiliar/transacoes/pesquisa-avancada'
      if (filtros.vencimentoInicio) {
        params.append('vencimentoInicio', filtros.vencimentoInicio)
      }
      if (filtros.vencimentoFim) {
        params.append('vencimentoFim', filtros.vencimentoFim)
      }
      if (filtros.valor) {
        params.append('valorComparacao', filtros.valorComparacao)
        params.append('valor', filtros.valor)
      }
      if (filtros.possuiAnexo !== null && filtros.possuiAnexo !== undefined) {
        params.append('possuiAnexo', filtros.possuiAnexo?.toString())
      }
      if (filtros.conta) {
        params.append('conta', filtros.conta)
      }
      if (filtros.statusPagamento !== null && filtros.statusPagamento !== undefined) {
        params.append('statusPagamento', filtros.statusPagamento.toString())
      }

      params.append('tipo', this.filtros.receitaDespesa)

      params.append('despesas_nao_efetivas', this.modalFiltrosAvancados.filtros.despesas_nao_efetivas.toString())

      const transacoes = await useApiStore().request(`${url}?${params.toString()}`, {
        method: 'post',
      })
      this.transacao = transacoes
    },
    async fetchServicos () {
      const servicos = await useApiStore().request('/gerencia/servico')
      return servicos
    },
    async uploadReceitas (file: File) {
      try {
        const formData = new FormData()
        formData.append('arquivo', file)
        formData.append('mes', this.modalImportarXml.mes.toString())
        formData.append('ano', this.modalImportarXml.ano.toString())
        formData.append('limparRegistros', this.modalImportarXml.limparRegistros.toString())

        // the url must be '/gerencia/livro-diario-auxiliar/importar-xml' or '/gerencia/livro-diario-auxiliar/importar-csv' based on filetype
        let url = '/gerencia/livro-diario-auxiliar/importar-xml'
        if (file.type === 'text/csv') {
          url = '/gerencia/livro-diario-auxiliar/importar-csv'
        }

        const response = await useApiStore().upload(url, formData)
        console.log('Resposta do servidor:', response)
      } catch (error) {
        console.error('Erro ao enviar arquivo:', error)
        throw error
      }
    },
    async uploadMultiplasReceitas (files: File[]) {
      try {
        const formData = new FormData()

        // Adicionar parâmetros de competência e configuração
        formData.append('mes', this.modalImportarXml.mes.toString())
        formData.append('ano', this.modalImportarXml.ano.toString())
        formData.append('limparRegistros', this.modalImportarXml.limparRegistros.toString())

        // Adicionar cada arquivo ao FormData
        files.forEach((file, index) => {
          formData.append(`arquivos[${index}]`, file)
        })

        const response = await useApiStore().upload('/gerencia/livro-diario-auxiliar/importar-multiplos', formData)

        return {
          sucesso: response.sucesso || 0,
          erros: response.erros || 0,
          detalhes: response.detalhes || [],
          mensagem: response.mensagem || 'Processamento concluído',
        }
      } catch (error) {
        console.error('Erro ao enviar arquivos:', error)
        throw error
      }
    },
    salvarReceita () {
      if (this.modalCadastrarReceitas.receita.id) {
        this.editarReceita()
      } else {
        this.cadastrarReceita()
      }
    },
    async cadastrarReceita () {
      await useApiStore().request('/gerencia/servico', {
        method: 'POST',
        body: this.modalCadastrarReceitas.receita,
      })
    },
    async editarReceita () {
      await useApiStore().request(`/gerencia/servico/${this.modalCadastrarReceitas.receita.id}`, {
        method: 'PUT',
        body: this.modalCadastrarReceitas.receita,
      })
    },
    abrirReceitaParaEdicao (receita: {nome: string, valor: number}) {
      this.modalCadastrarReceitas.receita = receita
      this.modalCadastrarReceitas.inputValor = receita.valor * 100
      this.modalCadastrarReceitas.titulo = 'Editar Receita'
      this.modalCadastrarReceitas.exibir = true
    },
    editarReceitaDoLancamento (index: number) {
      this.modalAdicionarReceita.exibir = true
      this.modalAdicionarReceita.receitaSelecionada = this.formNovaReceita.servicos[index].id
      this.modalAdicionarReceita.quantidade = this.formNovaReceita.servicos[index].quantidade
      this.modalAdicionarReceita.valor = this.formNovaReceita.servicos[index].valor
      this.modalAdicionarReceita.editMode = true
    },
    async downloadComprovantesPorCompetencia () {
      const ano = useStore().competencia.ano
      const mes = useStore().competencia.mes
      const url = this.getTransacaoUrl({ ano, mes, receitaDespesa: 'despesa' }) + '&download=true'

      try {
        const blob = await useApiStore().download(url)
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', `prestacao_de_contas_${mes}_${ano}.zip`)

        // Append to html link element page
        document.body.appendChild(link)

        // Start download
        link.click()

        // Clean up and remove the link
        link.parentNode?.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
      } catch (error) {
        console.error('Erro ao fazer download:', error)
        throw error
      }
    },
  },
  getters: {},
  persist: {
    paths: [],
  },
})
